import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../auth/supabaseClient';

const API_BASE =
  process.env.REACT_APP_API_BASE || 'https://helpmediy.org:3001/api';
const ROW_HEIGHT = 88;

const weightedRandomEntry = (entries) => {
  if (!entries.length) return null;
  const weighted = entries.map((entry) => ({
    entry,
    tickets: Math.max(1, Number(entry.tickets) || 1),
  }));
  const total = weighted.reduce((sum, item) => sum + item.tickets, 0);
  if (total <= 0) return entries[Math.floor(Math.random() * entries.length)];
  let roll = Math.floor(Math.random() * total);
  for (const item of weighted) {
    if (roll < item.tickets) return item.entry;
    roll -= item.tickets;
  }
  return weighted[weighted.length - 1]?.entry || entries[0];
};

const randomEntry = (entries, fallback) =>
  entries[Math.floor(Math.random() * entries.length)] || fallback || null;

const RaffleWinner = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [picking, setPicking] = useState(false);
  const [winner, setWinner] = useState(null);
  const [reelRows, setReelRows] = useState([]);
  const [reelTopIndex, setReelTopIndex] = useState(0);
  const [reelTransitionMs, setReelTransitionMs] = useState(0);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const ensureAudioContext = () => {
    if (audioRef.current) return audioRef.current;
    try {
      const context = new (window.AudioContext || window.webkitAudioContext)();
      audioRef.current = context;
      return context;
    } catch (err) {
      return null;
    }
  };

  const playTick = useCallback(() => {
    const context = ensureAudioContext();
    if (!context) return;
    const now = context.currentTime;
    const osc = context.createOscillator();
    const gain = context.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(380 + Math.random() * 120, now);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.035, now + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.045);
    osc.connect(gain);
    gain.connect(context.destination);
    osc.start(now);
    osc.stop(now + 0.05);
  }, []);

  const playFinal = useCallback(() => {
    const context = ensureAudioContext();
    if (!context) return;
    const now = context.currentTime;
    const tone = (freq, start, duration) => {
      const osc = context.createOscillator();
      const gain = context.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + start);
      gain.gain.setValueAtTime(0.0001, now + start);
      gain.gain.exponentialRampToValueAtTime(0.05, now + start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + start + duration);
      osc.connect(gain);
      gain.connect(context.destination);
      osc.start(now + start);
      osc.stop(now + start + duration + 0.02);
    };
    tone(660, 0, 0.18);
    tone(880, 0.14, 0.25);
  }, []);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/entries`);
      if (!res.ok) throw new Error('Failed to load raffle entries.');
      const payload = await res.json();
      const list = payload || [];
      setEntries(list);
      if (list.length > 0) {
        const first = list[0];
        setReelRows([first, randomEntry(list, first), randomEntry(list, first)]);
      } else {
        setReelRows([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to load raffle entries.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEntries();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [loadEntries]);

  const totalEntries = useMemo(
    () =>
      entries.reduce((sum, entry) => sum + Math.max(1, Number(entry.tickets) || 1), 0),
    [entries]
  );

  const buildSpinRows = useCallback((finalWinner) => {
    const spinCount = 90;
    const randomRows = Array.from({ length: spinCount }).map(() =>
      weightedRandomEntry(entries) || finalWinner
    );
    const before = randomEntry(entries, finalWinner) || finalWinner;
    const after = randomEntry(entries, finalWinner) || finalWinner;
    return [...randomRows, before, finalWinner, after];
  }, [entries]);

  const runSpinAnimation = useCallback(
    (finalWinner) => {
      const rows = buildSpinRows(finalWinner);
      setReelRows(rows);
      setReelTopIndex(0);
      setReelTransitionMs(0);
      const finalTopIndex = rows.length - 3;
      let step = 0;

      const tick = () => {
        step += 1;
        const progress = step / finalTopIndex;
        const easing = Math.pow(progress, 2.7);
        let delay = Math.floor(30 + easing * 320);
        const remaining = finalTopIndex - step;
        if (remaining <= 12) {
          delay += (12 - remaining) * 24;
        }
        if (remaining <= 6) {
          delay += (6 - remaining) * 34;
        }
        setReelTransitionMs(Math.max(45, Math.floor(delay * 0.92)));
        setReelTopIndex(step);
        playTick();

        if (step < finalTopIndex) {
          timerRef.current = setTimeout(tick, delay);
          return;
        }

        setWinner(finalWinner);
        setPicking(false);
        playFinal();
      };

      timerRef.current = setTimeout(tick, 40);
    },
    [buildSpinRows, playFinal, playTick]
  );

  const selectWinner = async () => {
    if (picking || entries.length === 0) return;
    setPicking(true);
    setWinner(null);
    setError('');
    ensureAudioContext();
    try {
      const { data } = await supabase.auth.getSession();
      const token = data.session?.access_token;
      if (!token) throw new Error('Missing session');

      const res = await fetch(`${API_BASE}/entries/raffle/pick-winner`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to select winner.');
      const payload = await res.json();
      runSpinAnimation(payload);
    } catch (err) {
      setPicking(false);
      setError(err.message || 'Failed to select winner.');
    }
  };

  const centerEntry = reelRows[reelTopIndex + 1];

  return (
    <div className="winner-page">
      <div className="winner-page-header">
        <h2>Winner Draw</h2>
        <Link to="/admin" className="raffle-link-button secondary">
          Back to Admin
        </Link>
      </div>

      <div className="winner-stats">
        <div className="winner-stat">
          <span>Total Entries</span>
          <strong>{loading ? '...' : totalEntries}</strong>
        </div>
        <div className="winner-stat">
          <span>Unique People</span>
          <strong>{loading ? '...' : entries.length}</strong>
        </div>
      </div>

      <div className={`slot-machine ${picking ? 'spinning' : ''} ${winner ? 'locked' : ''}`}>
        <div className="slot-title">
          {picking ? 'Spinning Wheel...' : 'Center row is the winner'}
        </div>
        <div className="reel-shell">
          <div className="reel-pointer" aria-hidden>
            ▶
          </div>
          <div className="reel-window">
            <div className="reel-center-line" aria-hidden />
            <div
              className="reel-track"
              style={{
                transform: `translateY(-${reelTopIndex * ROW_HEIGHT}px)`,
                transitionDuration: `${reelTransitionMs}ms`,
              }}
            >
              {reelRows.map((entry, index) => (
                <div key={`${entry?._id || 'entry'}-${index}`} className="reel-row">
                  <div className="reel-name">{entry?.name || '...'}</div>
                  <div className="reel-method">{entry?.method || ''}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="winner-actions">
        <button
          type="button"
          className="raffle-link-button"
          onClick={selectWinner}
          disabled={picking || loading || entries.length === 0}
        >
          {picking ? 'Selecting...' : 'Select Winner'}
        </button>
        <button
          type="button"
          className="raffle-link-button secondary"
          onClick={loadEntries}
          disabled={picking}
        >
          Refresh Entries
        </button>
      </div>

      {winner &&
        (winner._id ? (
          <Link
            to={`/raffle/entries/${winner._id}`}
            className="winner-reveal winner-reveal-link"
          >
            <h3>Winner: {centerEntry?.name || winner.name}</h3>
            <p>{centerEntry?.method || winner.method} Entry</p>
            <span>View full entry details</span>
          </Link>
        ) : (
          <div className="winner-reveal">
            <h3>Winner: {centerEntry?.name || winner.name}</h3>
            <p>{centerEntry?.method || winner.method} Entry</p>
          </div>
        ))}

      {error && <div className="error-banner">{error}</div>}
    </div>
  );
};

export default RaffleWinner;
